import React, { useState, useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import {
  DataTable,
  DataTableColumn,
  ColumnManagementConfig,
  SelectionConfig,
  PaginationConfig,
} from '@popety-io/ui'
import type { BuildingPermit } from '@popety-io/types'
import {
  getBuildingPermitsColumns,
  DEFAULT_VISIBLE_COLUMNS,
} from './buildingPermitsColumns'
import * as XLSX from 'xlsx'
import { useTheme } from 'styled-components'

interface BuildingPermitsDataTableProps {
  permits: BuildingPermit[]
  total: number
  loading?: boolean
  selectedPermit?: BuildingPermit | null
  onPermitSelect?: (permit: BuildingPermit) => void
  onRowHover?: (permit: BuildingPermit | null) => void
  onToggleSave?: (permit: BuildingPermit) => void
  savedPermitIds?: string[]
  onLoadMore?: () => void
  context?: 'main' | 'analysis' | 'estimate'
  currentPage?: number
  pageSize?: number
  onPageChange?: (page: number) => void
  onPageSizeChange?: (size: number) => void
}

const STORAGE_KEYS = {
  main: 'building_permits',
  analysis: 'building_permits_analysis',
  estimate: 'building_permits_estimate',
}

export const BuildingPermitsDataTable: React.FC<
  BuildingPermitsDataTableProps
> = ({
  permits,
  total,
  loading = false,
  selectedPermit,
  onPermitSelect,
  onRowHover,
  onToggleSave,
  savedPermitIds = [],
  onLoadMore,
  context = 'analysis',
  currentPage = 1,
  pageSize = 20,
  onPageChange,
  onPageSizeChange,
}) => {
  const { t } = useTranslation()
  const theme = useTheme()
  const storageKey = STORAGE_KEYS[context]

  // Column visibility state
  const [columnVisibility, setColumnVisibility] = useState<
    Record<string, boolean>
  >(() => {
    // Initialize with default visible columns for context
    const defaultVisible = DEFAULT_VISIBLE_COLUMNS[context]
    const allColumns = getBuildingPermitsColumns()
    const defaultVisibility = allColumns.reduce(
      (acc, col) => {
        acc[col.key] = defaultVisible.includes(col.key)
        return acc
      },
      {} as Record<string, boolean>
    )

    // Get stored visibility and merge with defaults to ensure new columns are included
    const stored = localStorage.getItem(`${storageKey}_column_visibility`)
    if (stored) {
      const storedVisibility = JSON.parse(stored)
      // Merge stored with defaults, ensuring 'actions' column is visible
      return { ...defaultVisibility, ...storedVisibility, actions: true }
    }

    return defaultVisibility
  })

  // Column widths state
  const [columnWidths, setColumnWidths] = useState<Record<string, number>>(
    () => {
      const stored = localStorage.getItem(`${storageKey}_column_widths`)
      return stored ? JSON.parse(stored) : {}
    }
  )

  // Column order state
  const [columnOrder, setColumnOrder] = useState<string[]>(() => {
    const defaultOrder = getBuildingPermitsColumns().map((col) => col.key)
    const stored = localStorage.getItem(`${storageKey}_column_order`)
    if (stored) {
      const storedOrder = JSON.parse(stored)
      // Ensure 'actions' column is in the order (add at end if missing)
      if (!storedOrder.includes('actions')) {
        return [...storedOrder, 'actions']
      }
      return storedOrder
    }
    return defaultOrder
  })

  // Selected rows state
  const [selectedRows, setSelectedRows] = useState<Set<string | number>>(
    new Set()
  )

  // Save preferences to localStorage
  useEffect(() => {
    localStorage.setItem(
      `${storageKey}_column_visibility`,
      JSON.stringify(columnVisibility)
    )
  }, [columnVisibility, storageKey])

  useEffect(() => {
    localStorage.setItem(
      `${storageKey}_column_widths`,
      JSON.stringify(columnWidths)
    )
  }, [columnWidths, storageKey])

  useEffect(() => {
    localStorage.setItem(
      `${storageKey}_column_order`,
      JSON.stringify(columnOrder)
    )
  }, [columnOrder, storageKey])

  // Get columns with current widths and save functionality
  const columns = useMemo(
    () =>
      getBuildingPermitsColumns(
        columnWidths,
        onToggleSave,
        savedPermitIds,
        context,
        theme,
        t
      ),
    [columnWidths, onToggleSave, savedPermitIds, context, theme, t]
  )

  // Get ordered columns
  const orderedColumns = useMemo(() => {
    if (!columns || columns.length === 0) {
      return columns
    }

    if (!columnOrder || columnOrder.length === 0) {
      return columns
    }

    return columnOrder
      .map((key) => columns.find((col) => col.key === key))
      .filter(Boolean) as DataTableColumn<BuildingPermit>[]
  }, [columns, columnOrder])

  // Column management config
  const columnManagement: ColumnManagementConfig = {
    visibility: columnVisibility,
    widths: columnWidths,
    order: columnOrder,
    onVisibilityChange: (columnId: string, visible: boolean) => {
      setColumnVisibility((prev) => ({ ...prev, [columnId]: visible }))
    },
    onResize: (columnId: string, width: number) => {
      setColumnWidths((prev) => ({ ...prev, [columnId]: width }))
    },
    onReorder: (fromIndex: number, toIndex: number) => {
      const newOrder = [...columnOrder]
      const [removed] = newOrder.splice(fromIndex, 1)
      newOrder.splice(toIndex, 0, removed)
      setColumnOrder(newOrder)
    },
    onReset: () => {
      // Reset to defaults for context
      const defaultVisible = DEFAULT_VISIBLE_COLUMNS[context]
      const allColumns = getBuildingPermitsColumns()
      const defaultVisibility = allColumns.reduce(
        (acc, col) => {
          acc[col.key] = defaultVisible.includes(col.key)
          return acc
        },
        {} as Record<string, boolean>
      )

      setColumnVisibility(defaultVisibility)
      setColumnWidths({})
      setColumnOrder(allColumns.map((col) => col.key))
    },
    storageKey,
  }

  // Selection config
  const selection: SelectionConfig<BuildingPermit> = {
    selectedRows,
    onRowSelect: (id: string | number, selected: boolean) => {
      setSelectedRows((prev) => {
        const newSet = new Set(prev)
        if (selected) {
          newSet.add(id)
        } else {
          newSet.delete(id)
        }
        return newSet
      })
    },
    onSelectAll: (selected: boolean) => {
      if (selected) {
        setSelectedRows(new Set(permits.map((p) => p.id)))
      } else {
        setSelectedRows(new Set())
      }
    },
    getRowId: (permit) => permit.id,
  }

  // Pagination config
  const pagination: PaginationConfig = {
    currentPage,
    pageSize,
    total,
    pageSizeOptions: [10, 20, 50, 100],
    onPageChange: onPageChange || (() => {}),
    onPageSizeChange: onPageSizeChange || (() => {}),
  }

  // Export config
  const exportConfig = {
    filename: `building_permits_${context}_export`,
    getExportData: (
      items: BuildingPermit[],
      columns: DataTableColumn<BuildingPermit>[]
    ) => {
      return items.map((permit) => {
        const rowData: Record<string, any> = {}

        columns.forEach((column) => {
          if (column.exportFormatter) {
            rowData[column.label] = column.exportFormatter(permit)
          }
        })

        return rowData
      })
    },
  }

  // Bulk actions
  const bulkActions = [
    {
      label: 'Export Selected',
      onClick: async (selectedItems: BuildingPermit[]) => {
        const data = selectedItems.map((permit) => {
          const rowData: Record<string, any> = {}

          orderedColumns.forEach((column) => {
            if (column.exportFormatter) {
              rowData[column.label] = column.exportFormatter(permit)
            }
          })

          return rowData
        })

        const worksheet = XLSX.utils.json_to_sheet(data)
        const workbook = XLSX.utils.book_new()
        XLSX.utils.book_append_sheet(workbook, worksheet, 'Building Permits')
        XLSX.writeFile(
          workbook,
          `building_permits_${context}_selected_${
            new Date().toISOString().split('T')[0]
          }.xlsx`
        )
      },
      variant: 'primary' as const,
    },
  ]

  return (
    <DataTable
      data={permits}
      columns={orderedColumns}
      loading={loading}
      pagination={pagination}
      selection={selection}
      columnManagement={columnManagement}
      bulkActions={bulkActions}
      enableExport={true}
      exportConfig={exportConfig}
      onRowClick={onPermitSelect}
      onRowHover={onRowHover}
      selectedRow={selectedPermit}
      emptyStateMessage={t('buildingPermits.messages.noPermitsFound')}
    />
  )
}
